import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import useOrganizationNames from './useOrganizationNames';
/**
 * CZNA-1576 — a proposal's originating institution as ONE readable string, for the places
 * that have to tell a user whom to write to: the proposal detail's "Instituce" field and the
 * edit route's "napište prosím původci návrhu" refusal. One formatter for both, so the
 * detail and the refusal cannot drift into naming the same library two different ways.
 *
 * <p>The format (porada 12. 8. 2026, bod 6; spec v3.1 §7 — a supervisor tells proposals
 * apart by sigla):
 * <ul>
 *   <li>"SIGLA – Name" (spaced en dash);</li>
 *   <li>the name alone for a library without a sigla, the sigla alone when only it is known;</li>
 *   <li>the raw UUID when the id resolves to nothing — the same deliberate "visibly broken"
 *       fallback as the proposals list's column (CZNA-597), so an unreadable lookup never
 *       looks like an empty field;</li>
 *   <li>an explicit "no institution (import)" text for a proposal that names no
 *       organization at all (import/API rows, see CZNA-603) — a blank or a dash there would
 *       read as "not loaded" rather than "there is none".</li>
 * </ul>
 */
var NO_ORGANIZATION_MESSAGE_ID = 'ui-czna-authorities.proposals.organization.none';
/** The label for an organization id that IS set; see the hook for the rules. */
export var formatOrganizationLabel = function (id, name, sigla) {
    var _a;
    if (sigla && name)
        return "".concat(sigla, " \u2013 ").concat(name);
    return (_a = name !== null && name !== void 0 ? name : sigla) !== null && _a !== void 0 ? _a : id;
};
var useOrganizationLabel = function () {
    var intl = useIntl();
    var _a = useOrganizationNames(), getOrganizationName = _a.getOrganizationName, getOrganizationSigla = _a.getOrganizationSigla, isLoading = _a.isLoading;
    var getOrganizationLabel = useCallback(function (id) { return (id
        ? formatOrganizationLabel(id, getOrganizationName(id), getOrganizationSigla(id))
        : intl.formatMessage({ id: NO_ORGANIZATION_MESSAGE_ID })); }, [intl, getOrganizationName, getOrganizationSigla]);
    return { getOrganizationLabel: getOrganizationLabel, isLoading: isLoading };
};
export default useOrganizationLabel;
